import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/admin';
import { Users, Search, Filter, Ban, Unlock, DollarSign, Eye, Edit, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatD10S } from '../../utils/formatters';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    minSpent: ''
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [actionLoading, setActionLoading] = useState(null); // userId when ban/unban in progress
  const ITEMS_PER_PAGE = 6;

  useEffect(() => {
    setCurrentPage(0);
    loadUsers();
  }, [filters]);

  useEffect(() => {
    if (allUsers.length > 0) {
      const start = currentPage * ITEMS_PER_PAGE;
      setUsers(allUsers.slice(start, start + ITEMS_PER_PAGE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getUsers(filters, 1000, 0); // Get all users, paginate client-side
      if (response?.success) {
        const allUsersData = response.users || [];
        setAllUsers(allUsersData);
        const start = currentPage * ITEMS_PER_PAGE;
        setUsers(allUsersData.slice(start, start + ITEMS_PER_PAGE));
      } else {
        setUsers([]);
        setAllUsers([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      // Set empty array on error to prevent crashes
      setUsers([]);
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  const handleEditUser = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  const handleBanUser = async (userId) => {
    if (!window.confirm('Are you sure you want to ban this user?')) return;
    const reason = window.prompt('Reason for ban:') || 'No reason provided';
    if (reason === null) return;

    setActionLoading(userId);
    try {
      const response = await adminApi.banUser(userId, { banType: 'total', reason });
      if (response?.success) {
        await loadUsers();
        window.alert('User banned successfully');
      } else {
        window.alert('Failed to ban user');
      }
    } catch (error) {
      window.alert('Error banning user: ' + (error?.message || 'Unknown error'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnbanUser = async (userId) => {
    if (!window.confirm('Are you sure you want to unban this user?')) return;

    setActionLoading(userId);
    try {
      const response = await adminApi.unbanUser(userId, { banType: 'all' });
      if (response?.success) {
        await loadUsers();
        window.alert('User unbanned successfully');
      } else {
        window.alert('Failed to unban user');
      }
    } catch (error) {
      window.alert('Error unbanning user: ' + (error?.message || 'Unknown error'));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <Users className="w-8 h-8 mr-3 text-blue-400" />
          User Management
        </h1>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 p-6 mb-6 admin-users-filters">
        <div className="admin-users-filters-header">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5" />
            <h2 className="text-lg font-semibold">Filters</h2>
          </div>
          <button
            type="button"
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="admin-users-filters-toggle"
          >
            {filtersOpen ? 'Hide' : 'Show'}
          </button>
        </div>
        <div className={`admin-users-filters-body ${filtersOpen ? 'is-open' : 'is-closed'}`}>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by username, email..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 px-4 py-2"
                />
              </div>
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-4 py-2"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="banned">Banned</option>
              <option value="inactive">Inactive</option>
            </select>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="px-4 py-2"
              placeholder="From Date"
            />
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="px-4 py-2"
              placeholder="To Date"
            />
          </div>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto"></div>
          <p className="mt-4">Loading users...</p>
        </div>
      ) : (
        <div className="bg-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase">Username</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase">KYC</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase">Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase">Referral</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{user.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{user.username}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs ${
                        user.is_active && !user.is_banned 
                          ? 'bg-green-500/20 text-green-400' 
                          : user.is_banned 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-gray-500/20'
                      }`}>
                        {user.is_banned ? 'Banned' : user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs ${
                        user.is_verified ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20'
                      }`}>
                        {user.is_verified ? 'Verified' : 'Not Verified'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-400">
                      {formatD10S(user.d10s_balance || 0)} DIOS
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {user.referral_code || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewUser(user.id)}
                          className="p-1 text-blue-400 hover:text-blue-300"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditUser(user.id)}
                          className="p-1 text-yellow-400 hover:text-yellow-300"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {user.is_banned ? (
                          <button
                            onClick={() => handleUnbanUser(user.id)}
                            disabled={actionLoading === user.id}
                            className="p-1 text-green-400 hover:text-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Unban"
                          >
                            <Unlock className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBanUser(user.id)}
                            disabled={actionLoading === user.id}
                            className="p-1 text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Ban"
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && users.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          No users found
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && allUsers.length > ITEMS_PER_PAGE && (
        <div className="flex items-center justify-center space-x-4 mt-6">
          <button
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
              currentPage === 0
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Previous
          </button>
          <span className="text-gray-400 text-sm">
            Page {currentPage + 1} of {Math.ceil(allUsers.length / ITEMS_PER_PAGE)}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(Math.ceil(allUsers.length / ITEMS_PER_PAGE) - 1, currentPage + 1))}
            disabled={currentPage >= Math.ceil(allUsers.length / ITEMS_PER_PAGE) - 1}
            className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
              currentPage >= Math.ceil(allUsers.length / ITEMS_PER_PAGE) - 1
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Next
            <ChevronRight className="w-5 h-5 ml-1" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
